import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCamera,
  faCheck,
  faImages,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { IMAGE_JPEG_QUALITY, MAX_IMAGE_DIMENSION } from './types'
import { useAutoDismiss } from './useAutoDismiss'

type PhotoCaptureProps = {
  open: boolean
  title?: string
  disabled?: boolean
  onClose: () => void
  /** Called with a JPEG data URL for each shutter press (modal stays open). */
  onCapture: (dataUrl: string) => void
  /** Opens the system gallery/file picker; camera stays running. */
  onPickGallery?: () => void
}

function cameraErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : ''
  const name = err instanceof Error ? err.name : ''
  if (name === 'NotAllowedError' || /permission|permissions policy|not allowed/i.test(raw)) {
    return 'Camera is blocked for this site. Allow Camera in browser site settings, or use Gallery.'
  }
  if (name === 'NotFoundError' || /not found|no device/i.test(raw)) {
    return 'No camera found on this device. Use Gallery instead.'
  }
  return raw || 'Camera unavailable. Use Gallery instead.'
}

/** Draw the current video frame to a JPEG data URL, capped at MAX_IMAGE_DIMENSION. */
export function captureVideoFrame(video: HTMLVideoElement): string {
  const vw = video.videoWidth || 0
  const vh = video.videoHeight || 0
  if (!vw || !vh) throw new Error('Camera is not ready yet.')
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(vw, vh))
  const width = Math.max(1, Math.round(vw * scale))
  const height = Math.max(1, Math.round(vh * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Unable to capture photo')
  ctx.drawImage(video, 0, 0, width, height)
  const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_JPEG_QUALITY)
  canvas.width = 0
  canvas.height = 0
  return dataUrl
}

export const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  open,
  title = 'Take photo',
  disabled = false,
  onClose,
  onCapture,
  onPickGallery,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [starting, setStarting] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [flash, setFlash] = useState(false)
  const [capturedCount, setCapturedCount] = useState(0)
  useAutoDismiss(cameraError, () => setCameraError(null), 5000)

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    const video = videoRef.current
    if (video) video.srcObject = null
  }, [])

  useEffect(() => {
    if (!open) {
      stopCamera()
      setStarting(false)
      setCapturing(false)
      setCameraError(null)
      setFlash(false)
      setCapturedCount(0)
      return
    }

    let cancelled = false
    setStarting(true)
    setCameraError(null)
    setCapturedCount(0)

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) {
          setCameraError('Camera API not available. Use Gallery instead.')
          setStarting(false)
        }
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 960 },
          },
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          await video.play().catch(() => {
            /* autoplay policies — playsInline + muted usually ok */
          })
        }
      } catch (err: unknown) {
        if (!cancelled) setCameraError(cameraErrorMessage(err))
      } finally {
        if (!cancelled) setStarting(false)
      }
    }

    void start()
    return () => {
      cancelled = true
      stopCamera()
    }
  }, [open, stopCamera])

  const handleShutter = () => {
    if (disabled || capturing || starting || cameraError) return
    const video = videoRef.current
    if (!video) return
    setCapturing(true)
    try {
      const dataUrl = captureVideoFrame(video)
      setFlash(true)
      window.setTimeout(() => setFlash(false), 120)
      onCapture(dataUrl)
      setCapturedCount((n) => n + 1)
    } catch (err: unknown) {
      setCameraError(err instanceof Error ? err.message : 'Could not capture photo.')
    } finally {
      setCapturing(false)
    }
  }

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-stretch justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex h-[100dvh] max-h-[100dvh] w-full max-w-none flex-col overflow-hidden rounded-none bg-base-100 shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:rounded-box"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between border-b border-base-200 px-4 py-3 shrink-0">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <FontAwesomeIcon icon={faCamera} />
            {title}
          </h3>
          <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
          <div className="relative min-h-0 flex-1 overflow-hidden rounded-box bg-neutral sm:mx-auto sm:aspect-[3/4] sm:w-full sm:max-h-[min(28rem,55vh)] sm:flex-none">
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full object-cover"
              muted
              playsInline
              autoPlay
            />
            {starting ? (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral/60 text-neutral-content">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl" />
              </div>
            ) : null}
            {flash ? <div className="pointer-events-none absolute inset-0 bg-white/80" /> : null}
            {capturedCount > 0 ? (
              <span className="absolute top-3 left-3 badge badge-success gap-1 shadow">
                <FontAwesomeIcon icon={faCheck} />
                {capturedCount} added
              </span>
            ) : null}
          </div>

          {cameraError ? <div className="alert alert-warning shrink-0 py-2 text-sm">{cameraError}</div> : null}

          <div className="flex shrink-0 flex-col gap-2">
            <button
              type="button"
              className="btn btn-primary btn-lg w-full"
              disabled={disabled || starting || capturing || Boolean(cameraError)}
              onClick={handleShutter}
            >
              {capturing ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faCamera} />
              )}
              Capture
            </button>
            <div className="flex gap-2">
              {onPickGallery ? (
                <button type="button" className="btn btn-outline flex-1" disabled={disabled} onClick={onPickGallery}>
                  <FontAwesomeIcon icon={faImages} />
                  Gallery
                </button>
              ) : null}
              <button type="button" className="btn btn-neutral flex-1" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default PhotoCapture
